import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Snackbar, Slide, Alert as MuiAlert } from "@mui/material";

/**
 * The app's toast.
 *
 * Two things it has to get right that a page-local Snackbar cannot:
 *
 *  - It outlives navigation. "Signed in" and "Signed out" are both raised by a
 *    screen that unmounts a moment later, so the toast is mounted once at the
 *    app root and driven through context instead of by the page.
 *  - It escapes the auth pages' stacking context. `.pj-auth` is
 *    `isolation: isolate`, which traps a Snackbar's z-index and lets the fixed
 *    AppBar paint over it.
 */

/** iOS system colours (light appearance). */
export const IOS = {
  green: "#1c9d3c",
  red: "#b42720",
  blue: "#0d67c8",
  orange: "#d57f06",
};

const TONE = {
  success: { bg: IOS.green, glow: "rgba(52, 199, 89, 0.45)" },
  error: { bg: IOS.red, glow: "rgba(255, 59, 48, 0.45)" },
  info: { bg: IOS.blue, glow: "rgba(0, 122, 255, 0.45)" },
  warning: { bg: IOS.orange, glow: "rgba(255, 149, 0, 0.45)" },
};

/* Declared at module scope: inline, the Snackbar would remount the transition
   on every render and restart the animation mid-flight. */
const SlideDown = (props) => <Slide {...props} direction="down" />;

/* Decelerating on the way in, brisk on the way out — the asymmetry is what
   makes it read as smooth rather than mechanical. */
const TIMEOUT = { enter: 420, exit: 240 };
const EASING = {
  enter: "cubic-bezier(0.22, 1, 0.36, 1)",
  exit: "cubic-bezier(0.4, 0, 1, 1)",
};

/**
 * The toast itself. Shared with the auth pages so a validation error and a
 * "Signed in" confirmation are visually the same object.
 */
export const AppToast = ({
  open,
  onClose,
  severity = "error",
  autoHideDuration = 4000,
  children,
}) => {
  const tone = TONE[severity] || TONE.info;

  return (
    <Snackbar
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      autoHideDuration={autoHideDuration}
      TransitionComponent={SlideDown}
      transitionDuration={TIMEOUT}
      TransitionProps={{ easing: EASING }}
      // Clear of the fixed 64px AppBar the layout offsets the page by.
      sx={{ top: { xs: "76px", sm: "84px" } }}
    >
      <MuiAlert
        variant="filled"
        onClose={onClose}
        severity={severity}
        sx={{
          minWidth: "300px",
          maxWidth: "440px",
          alignItems: "center",
          padding: "6px 16px",
          borderRadius: "14px",
          backgroundColor: tone.bg,
          color: "#ffffff",
          fontSize: "14.5px",
          fontWeight: 600,
          letterSpacing: "0.1px",
          boxShadow: `0 10px 20px -10px ${tone.glow}, 0 22px 48px -22px rgba(2, 10, 20, 0.55)`,
          '& .MuiAlert-icon': { color: '#ffffff', opacity: 1 },
          '& .MuiAlert-action': { paddingTop: 0 },
          '& .MuiAlert-action .MuiSvgIcon-root': { color: '#ffffff' },
        }}
      >
        {children}
      </MuiAlert>
    </Snackbar>
  );
};

const ToastContext = createContext(null);

/** `const toast = useToast(); toast.success("Signed in")` */
export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Never throw over a toast — a missing provider should not take a page down.
    return { show: () => {}, success: () => {}, error: () => {}, info: () => {} };
  }
  return ctx;
};

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState({ open: false, message: "", severity: "info", duration: 4000 });
  const closing = useRef(null);

  const show = useCallback((message, severity = "info", duration = 4000) => {
    if (!message) return;
    // Replacing a visible toast: let it retract first so the two do not cross.
    clearTimeout(closing.current);
    setToast((prev) => {
      if (!prev.open) return { open: true, message, severity, duration };
      closing.current = setTimeout(
        () => setToast({ open: true, message, severity, duration }),
        TIMEOUT.exit
      );
      return { ...prev, open: false };
    });
  }, []);

  const api = useMemo(
    () => ({
      show,
      success: (message, duration) => show(message, "success", duration),
      error: (message, duration) => show(message, "error", duration),
      info: (message, duration) => show(message, "info", duration),
    }),
    [show]
  );

  const handleClose = useCallback((event, reason) => {
    if (reason === "clickaway") return;
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <AppToast
        open={toast.open}
        onClose={handleClose}
        severity={toast.severity}
        autoHideDuration={toast.duration}
      >
        {toast.message}
      </AppToast>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
